import { type ReactNode, type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fadeStyle, useResizeObserver } from "@tellescope/react-hooks";
import { cn } from "../../lib/utils";
import { TableCellContextProvider, type TableCellContextValue } from "./TableCellContext";
import { useTableContext } from "./TableContext";
import type { TableColumnOverflow } from "./types";

export interface TableCellProps {
  columnId: string;
  children: ReactNode;
}

const ALIGN_CLASS = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
} as const;

const SCROLL_IDLE_MS = 1000;
/** `text-sm` line height, which is what a clamped cell measures its available lines in. */
const LINE_HEIGHT_PX = 20;
/** The `py-1.5` a wrapped cell carries, excluded from its available lines. */
const WRAPPED_CELL_VERTICAL_PADDING_PX = 12;

// Cell renderers across the app hard-code single-line truncation. The wrapping modes override that
// on every descendant so a column can wrap without each renderer knowing about overflow modes.
const UNCLIP_DESCENDANTS = "[&_.truncate]:!overflow-visible [&_.truncate]:![text-overflow:clip]";
const WRAP_DESCENDANTS = `${UNCLIP_DESCENDANTS} [&_.truncate]:!whitespace-normal`;
const SCROLLBAR_CLASS = "scrollbar-thin scrollbar-track-transparent";

const isScrollMode = (overflow: TableColumnOverflow) =>
  overflow === "scroll-horizontal" || overflow === "scroll-vertical";

/** Whether the element scrolls vertically and its end is still below the fold. */
const isScrolledShort = (element: HTMLElement): boolean => {
  const { scrollTop, scrollHeight, clientHeight } = element;
  return scrollHeight > clientHeight + 1 && scrollTop + clientHeight < scrollHeight - 1;
};

const availableLines = (element: HTMLElement) =>
  Math.max(1, Math.floor((element.clientHeight - WRAPPED_CELL_VERTICAL_PADDING_PX) / LINE_HEIGHT_PX));

/**
 * Every mode fills the row's height and handles what overflows in its own direction. In a fixed-height
 * row that height is a single line; in a row an `expand` column has grown, the truncating mode wraps into
 * the extra lines before clamping, while the horizontally-scrolling mode stays a single centred line.
 * Every mode clips whatever it does not scroll, so no content ever paints over a neighbouring column.
 */
export const TableCell = ({ columnId, children }: TableCellProps) => {
  const { layoutById, rowHeight, expandMaxHeight, hasExpandColumn, columnContentWidths } = useTableContext();
  const layout = layoutById[columnId];
  const overflow = layout?.overflow ?? "truncate";
  const contentWidth = columnContentWidths[columnId];
  const maxContentHeight =
    overflow === "expand" && expandMaxHeight !== undefined
      ? expandMaxHeight - WRAPPED_CELL_VERTICAL_PADDING_PX
      : undefined;
  const cellContext = useMemo<TableCellContextValue>(
    () => ({ overflow, contentWidth, maxContentHeight }),
    [overflow, contentWidth, maxContentHeight]
  );
  const align = ALIGN_CLASS[layout?.align ?? "left"];
  const clampsToFill = hasExpandColumn && overflow === "truncate";

  const [isScrolling, setIsScrolling] = useState(false);
  const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether there is more below, so the bottom edge can fade the way `PaginatedList` does. Tracked on
  // scroll as well as on resize, since scrolling to the end is what clears it.
  const { ref: clipRef, value: measuredClipped } = useResizeObserver<boolean>(isScrolledShort, false);
  const [scrolledClipped, setScrolledClipped] = useState<boolean>();
  const isClipped = scrolledClipped ?? measuredClipped;
  const handleScroll = useCallback((event: UIEvent<HTMLElement>) => {
    setIsScrolling(true);
    setScrolledClipped(isScrolledShort(event.currentTarget));
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    idleTimeoutRef.current = setTimeout(() => setIsScrolling(false), SCROLL_IDLE_MS);
  }, []);
  useEffect(
    () => () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    },
    []
  );

  // Only a clamped cell attaches this, so fixed-height tables observe nothing.
  const { ref: clampRef, value: lines } = useResizeObserver<number>(availableLines, 1);

  const baseClassName = cn("flex min-w-0 px-2 text-sm [&>*]:min-w-0", align);

  if (isScrollMode(overflow)) {
    // The scroll area is taken out of flow so its content cannot grow the row: the row's height comes
    // from its other cells (or `rowHeight`), and this cell fills whatever that turns out to be.
    return (
      <div
        data-slot="table-cell"
        data-testid={`table-cell-${columnId}`}
        data-overflow={overflow}
        className="relative min-w-0 overflow-hidden"
        style={{ minHeight: rowHeight }}
      >
        <div
          ref={overflow === "scroll-vertical" ? clipRef : undefined}
          className={cn(
            baseClassName,
            "absolute inset-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            SCROLLBAR_CLASS,
            isScrolling ? "scrollbar-thumb-foreground/50" : "scrollbar-thumb-transparent",
            overflow === "scroll-horizontal"
              ? cn("items-center overflow-x-auto overflow-y-hidden whitespace-nowrap", UNCLIP_DESCENDANTS)
              : cn(
                  // Auto margins centre short content yet collapse when it overflows, keeping the top reachable.
                  "items-start [&>*]:my-auto overflow-y-auto overflow-x-hidden whitespace-normal break-words py-1.5",
                  WRAP_DESCENDANTS
                )
          )}
          // Fades the bottom edge while there is more below, so a cut-off cell reads as scrollable.
          style={overflow === "scroll-vertical" ? fadeStyle({ faded: isClipped }) : undefined}
          onScroll={handleScroll}
          // A region that scrolls must be reachable from the keyboard to be scrollable without a mouse.
          tabIndex={0}
        >
          <TableCellContextProvider value={cellContext}>{children}</TableCellContextProvider>
        </div>
      </div>
    );
  }

  if (overflow === "expand") {
    // A capped expand cell grows the row up to the cap and scrolls the rest, so it is scrollable in the
    // same way as a scroll-vertical cell (focusable, thumb shown while scrolling).
    const isCapped = expandMaxHeight !== undefined;
    return (
      <div
        ref={isCapped ? clipRef : undefined}
        data-slot="table-cell"
        data-testid={`table-cell-${columnId}`}
        data-overflow={overflow}
        className={cn(
          baseClassName,
          "whitespace-normal break-words py-1.5",
          isCapped
            ? cn(
                // `items-start` keeps the content at its natural height so the cap has something to
                // scroll; `items-center` would stretch it to the box and never overflow.
                "items-start overflow-y-auto overflow-x-hidden rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                SCROLLBAR_CLASS,
                isScrolling ? "scrollbar-thumb-foreground/50" : "scrollbar-thumb-transparent"
              )
            : // `clip` rather than `hidden`, which would force the vertical axis to `auto` and make an
              // uncapped cell a scroll container its growing content could never fill.
              "items-center overflow-x-clip",
          WRAP_DESCENDANTS
        )}
        style={{
          minHeight: rowHeight,
          maxHeight: expandMaxHeight,
          ...(isCapped ? fadeStyle({ faded: isClipped }) : {}),
        }}
        onScroll={isCapped ? handleScroll : undefined}
        tabIndex={isCapped ? 0 : undefined}
      >
        <TableCellContextProvider value={cellContext}>{children}</TableCellContextProvider>
      </div>
    );
  }

  if (clampsToFill) {
    return (
      <div
        ref={clampRef}
        data-slot="table-cell"
        data-testid={`table-cell-${columnId}`}
        data-overflow={overflow}
        className={cn(
          baseClassName,
          "items-center overflow-hidden whitespace-normal break-words py-1.5",
          WRAP_DESCENDANTS
        )}
        style={{ minHeight: rowHeight }}
      >
        {/* A line clamp is the only ellipsis CSS offers past one line, and it needs the line count, which
            is whatever the row's height leaves room for. */}
        <div
          className="min-w-0 max-w-full overflow-hidden"
          style={{ display: "-webkit-box", WebkitBoxOrient: "vertical", WebkitLineClamp: lines }}
        >
          <TableCellContextProvider value={cellContext}>{children}</TableCellContextProvider>
        </div>
      </div>
    );
  }

  return (
    <div
      data-slot="table-cell"
      data-testid={`table-cell-${columnId}`}
      data-overflow={overflow}
      className={cn(baseClassName, "items-center overflow-hidden whitespace-nowrap")}
      style={{ minHeight: rowHeight }}
    >
      {/* Text dropped straight into a flex container becomes an anonymous item that never ellipsizes, so
          the single-line mode gives it a block to clip in. Renderers that truncate themselves are unaffected. */}
      <div className="min-w-0 max-w-full truncate">
        <TableCellContextProvider value={cellContext}>{children}</TableCellContextProvider>
      </div>
    </div>
  );
};
